import React, { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Phone, Mail, MapPin, MessageCircle, Home, ChevronRight, Building2, Waves, Key, ArrowRight } from 'lucide-react';

// SEO Page Component for neighborhood/category pages
const SEOPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const { language, changeLanguage } = useLanguage();
  const isRTL = language === 'he';

  // WhatsApp
  const WHATSAPP_NUMBER = "972523570058";
  const openWhatsApp = (message) => {
    const defaultMsg = language === 'fr' 
      ? 'Bonjour Vanessa, je vous contacte depuis Bayitfull. Je recherche un bien immobilier à Netanya.'
      : language === 'he'
      ? 'שלום ונסה, אני פונה אליך מ-Bayitfull. אני מחפש/ת נכס בנתניה.'
      : 'Hello Vanessa, I am contacting you from Bayitfull. I am looking for a property in Netanya.';
    const encodedMsg = encodeURIComponent(message || defaultMsg);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMsg}`, '_blank');
  };

  // Page configurations
  const pageConfigs = {
    'immobilier-netanya': {
      fr: {
        title: 'Immobilier Netanya | BAYITFULL - Agence Immobilière Francophone',
        description: 'Découvrez les meilleures opportunités immobilières à Netanya avec BAYITFULL. Vanessa Benaim Kakun, votre conseillère francophone spécialisée en vente et location.',
        h1: 'Immobilier à Netanya',
        intro: 'Netanya est une ville côtière israélienne réputée pour ses plages magnifiques, son climat méditerranéen et sa forte communauté francophone. BAYITFULL vous accompagne dans tous vos projets immobiliers à Netanya.',
        content: `
          <h2>Pourquoi investir à Netanya ?</h2>
          <p>Netanya offre un cadre de vie exceptionnel avec ses 14 km de plages de sable fin, ses promenades en bord de mer et son centre-ville animé. La ville attire de nombreux Français et francophones grâce à sa qualité de vie et ses prix plus accessibles que Tel Aviv.</p>
          
          <h2>Les quartiers prisés de Netanya</h2>
          <p>Netanya se compose de plusieurs quartiers aux caractères distincts : <strong>Ir Yamim</strong> pour le luxe en bord de mer, <strong>Nat 600</strong> pour les projets neufs modernes, le <strong>centre-ville</strong> pour l'animation et les commodités, et le <strong>front de mer</strong> pour les vues imprenables sur la Méditerranée.</p>
          
          <h2>Votre projet immobilier à Netanya</h2>
          <p>Que vous recherchiez un appartement à acheter, un bien à louer ou un investissement locatif, Vanessa Benaim Kakun vous conseille avec une expertise locale approfondie. BAYITFULL propose un accompagnement personnalisé en français de la recherche à la signature.</p>
        `
      },
      en: {
        title: 'Netanya Real Estate | BAYITFULL - French-Speaking Real Estate Agency',
        description: 'Discover the best real estate opportunities in Netanya with BAYITFULL. Vanessa Benaim Kakun, your specialized French-speaking advisor for sales and rentals.',
        h1: 'Real Estate in Netanya',
        intro: 'Netanya is an Israeli coastal city renowned for its beautiful beaches, Mediterranean climate, and large French-speaking community. BAYITFULL supports you in all your real estate projects in Netanya.',
        content: `
          <h2>Why invest in Netanya?</h2>
          <p>Netanya offers an exceptional living environment with its 14 km of sandy beaches, seaside promenades, and lively city center. The city attracts many French and French-speaking people thanks to its quality of life and more affordable prices than Tel Aviv.</p>
          
          <h2>Popular neighborhoods in Netanya</h2>
          <p>Netanya consists of several neighborhoods with distinct characters: <strong>Ir Yamim</strong> for seaside luxury, <strong>Nat 600</strong> for modern new projects, the <strong>city center</strong> for amenities, and the <strong>seafront</strong> for stunning Mediterranean views.</p>
          
          <h2>Your real estate project in Netanya</h2>
          <p>Whether you're looking to buy an apartment, rent a property, or make a rental investment, Vanessa Benaim Kakun advises you with in-depth local expertise. BAYITFULL offers personalized support from search to signing.</p>
        `
      },
      he: {
        title: 'נדל"ן נתניה | BAYITFULL - סוכנות נדל"ן',
        description: 'גלו את ההזדמנויות הטובות ביותר בנדל"ן בנתניה עם BAYITFULL. ונסה בנאים קקון, יועצת מומחית למכירה והשכרה.',
        h1: 'נדל"ן בנתניה',
        intro: 'נתניה היא עיר חוף ישראלית הידועה בחופיה היפים, האקלים הים תיכוני שלה וקהילה דוברת צרפתית גדולה. BAYITFULL מלווה אתכם בכל פרויקטי הנדל"ן שלכם בנתניה.',
        content: `
          <h2>למה להשקיע בנתניה?</h2>
          <p>נתניה מציעה סביבת מגורים יוצאת דופן עם 14 ק"מ של חופי חול, טיילות לחוף הים ומרכז עיר תוסס. העיר מושכת רבים מצרפת ודוברי צרפתית בזכות איכות החיים והמחירים הנגישים יותר מתל אביב.</p>
          
          <h2>שכונות פופולריות בנתניה</h2>
          <p>נתניה מורכבת ממספר שכונות עם אופי שונה: <strong>עיר ימים</strong> ליוקרה לחוף הים, <strong>נת 600</strong> לפרויקטים חדשים מודרניים, <strong>מרכז העיר</strong> לנוחות, ו<strong>חזית הים</strong> לנופים מרהיבים לים התיכון.</p>
          
          <h2>פרויקט הנדל"ן שלכם בנתניה</h2>
          <p>בין אם אתם מחפשים לקנות דירה, לשכור נכס או לבצע השקעה להשכרה, ונסה בנאים קקון מייעצת לכם עם מומחיות מקומית מעמיקה.</p>
        `
      },
      ru: {
        title: 'Недвижимость Нетания | BAYITFULL - Агентство Недвижимости',
        description: 'Откройте лучшие возможности недвижимости в Нетании с BAYITFULL. Ванесса Бенаим Какун, ваш специализированный консультант по продаже и аренде.',
        h1: 'Недвижимость в Нетании',
        intro: 'Нетания — израильский прибрежный город, известный своими прекрасными пляжами, средиземноморским климатом и большой франкоязычной общиной. BAYITFULL поддерживает вас во всех ваших проектах недвижимости в Нетании.',
        content: `
          <h2>Почему инвестировать в Нетанию?</h2>
          <p>Нетания предлагает исключительную среду обитания с 14 км песчаных пляжей, приморскими прогулками и оживленным центром города.</p>
          
          <h2>Популярные районы Нетании</h2>
          <p>Нетания состоит из нескольких районов с разным характером: <strong>Ир Ямим</strong> для роскоши на берегу моря, <strong>Нат 600</strong> для современных новых проектов.</p>
        `
      }
    },
    'ir-yamim': {
      fr: {
        title: 'Immobilier Ir Yamim Netanya | BAYITFULL - Quartier de Luxe',
        description: 'Ir Yamim : le quartier le plus prisé de Netanya. Appartements de luxe, penthouses et villas avec vue mer. BAYITFULL, votre expert immobilier francophone.',
        h1: 'Immobilier à Ir Yamim',
        intro: 'Ir Yamim (עיר ימים, "Ville des Mers") est le quartier le plus luxueux de Netanya, situé en bord de mer au sud de la ville. Ce quartier résidentiel haut de gamme offre des propriétés d\'exception avec vue sur la Méditerranée.',
        content: `
          <h2>Ir Yamim : le quartier premium de Netanya</h2>
          <p>Ir Yamim se distingue par ses résidences de standing, ses espaces verts soignés et sa proximité immédiate avec la plage. Le quartier attire une clientèle internationale recherchant le calme et le luxe.</p>
          
          <h2>Types de biens à Ir Yamim</h2>
          <p>On trouve à Ir Yamim des <strong>appartements de 3 à 6 pièces</strong>, des <strong>penthouses avec terrasse panoramique</strong>, et des <strong>villas de prestige</strong>. La plupart des biens offrent une vue mer partielle ou totale.</p>
          
          <h2>Investir à Ir Yamim</h2>
          <p>Les prix à Ir Yamim sont parmi les plus élevés de Netanya, reflétant la qualité exceptionnelle des biens et l'emplacement privilégié. C'est un investissement sûr dans l'immobilier de luxe israélien.</p>
        `
      },
      en: {
        title: 'Ir Yamim Real Estate Netanya | BAYITFULL - Luxury Neighborhood',
        description: 'Ir Yamim: Netanya\'s most sought-after neighborhood. Luxury apartments, penthouses and villas with sea views. BAYITFULL, your French-speaking real estate expert.',
        h1: 'Real Estate in Ir Yamim',
        intro: 'Ir Yamim (עיר ימים, "City of Seas") is the most luxurious neighborhood in Netanya, located by the sea south of the city. This upscale residential area offers exceptional properties with Mediterranean views.',
        content: `
          <h2>Ir Yamim: Netanya's premium neighborhood</h2>
          <p>Ir Yamim stands out for its luxury residences, manicured green spaces, and immediate proximity to the beach. The neighborhood attracts an international clientele seeking peace and luxury.</p>
          
          <h2>Property types in Ir Yamim</h2>
          <p>In Ir Yamim you'll find <strong>3 to 6-room apartments</strong>, <strong>penthouses with panoramic terraces</strong>, and <strong>prestigious villas</strong>. Most properties offer partial or full sea views.</p>
        `
      },
      he: {
        title: 'נדל"ן עיר ימים נתניה | BAYITFULL - שכונת יוקרה',
        description: 'עיר ימים: השכונה המבוקשת ביותר בנתניה. דירות יוקרה, פנטהאוזים ווילות עם נוף לים. BAYITFULL, המומחה שלכם לנדל"ן.',
        h1: 'נדל"ן בעיר ימים',
        intro: 'עיר ימים היא השכונה היוקרתית ביותר בנתניה, ממוקמת לחוף הים בדרום העיר. אזור מגורים יוקרתי זה מציע נכסים יוצאי דופן עם נוף לים התיכון.',
        content: `
          <h2>עיר ימים: השכונה הפרימיום של נתניה</h2>
          <p>עיר ימים בולטת בדירות היוקרה שלה, השטחים הירוקים המטופחים והקרבה המיידית לחוף. השכונה מושכת לקוחות בינלאומיים המחפשים שקט ויוקרה.</p>
        `
      },
      ru: {
        title: 'Недвижимость Ир Ямим Нетания | BAYITFULL',
        description: 'Ир Ямим: самый престижный район Нетании. Роскошные квартиры, пентхаусы и виллы с видом на море.',
        h1: 'Недвижимость в Ир Ямим',
        intro: 'Ир Ямим — самый роскошный район Нетании, расположенный у моря на юге города.',
        content: ''
      }
    },
    'nat-600': {
      fr: {
        title: 'Immobilier Nat 600 Netanya | BAYITFULL - Projets Neufs',
        description: 'Nat 600 : nouveau quartier moderne de Netanya. Projets neufs, résidences contemporaines et opportunités d\'investissement. BAYITFULL votre expert.',
        h1: 'Immobilier à Nat 600',
        intro: 'Nat 600 est un quartier en plein développement au nord de Netanya, caractérisé par ses nombreux projets de construction neufs et ses résidences modernes. C\'est le secteur idéal pour les investisseurs et les familles.',
        content: `
          <h2>Nat 600 : le nouveau visage de Netanya</h2>
          <p>Le quartier Nat 600 connaît une transformation spectaculaire avec de nombreuses tours résidentielles modernes. Les promoteurs y construisent des appartements aux normes les plus récentes avec des finitions haut de gamme.</p>
          
          <h2>Avantages d'acheter à Nat 600</h2>
          <p>Acheter sur plan ou dans un projet neuf à Nat 600 permet de bénéficier de <strong>prix attractifs</strong>, de <strong>garanties constructeur</strong> et d'<strong>appartements modernes</strong> avec parking et mamad (pièce sécurisée).</p>
          
          <h2>Projets en cours à Nat 600</h2>
          <p>Plusieurs promoteurs de renom construisent actuellement à Nat 600. Contactez BAYITFULL pour connaître les projets disponibles et les opportunités d'achat sur plan.</p>
        `
      },
      en: {
        title: 'Nat 600 Real Estate Netanya | BAYITFULL - New Projects',
        description: 'Nat 600: Netanya\'s new modern neighborhood. New construction projects, contemporary residences and investment opportunities. BAYITFULL your expert.',
        h1: 'Real Estate in Nat 600',
        intro: 'Nat 600 is a rapidly developing neighborhood in northern Netanya, characterized by numerous new construction projects and modern residences. It\'s the ideal area for investors and families.',
        content: `
          <h2>Nat 600: The new face of Netanya</h2>
          <p>The Nat 600 neighborhood is undergoing a spectacular transformation with numerous modern residential towers. Developers are building apartments to the latest standards with high-end finishes.</p>
        `
      },
      he: {
        title: 'נדל"ן נת 600 נתניה | BAYITFULL - פרויקטים חדשים',
        description: 'נת 600: שכונה מודרנית חדשה בנתניה. פרויקטים חדשים, מגורים עכשוויים והזדמנויות השקעה.',
        h1: 'נדל"ן בנת 600',
        intro: 'נת 600 היא שכונה מתפתחת בצפון נתניה, המאופיינת בפרויקטי בנייה חדשים רבים ומגורים מודרניים.',
        content: ''
      },
      ru: {
        title: 'Недвижимость Нат 600 Нетания | BAYITFULL',
        description: 'Нат 600: новый современный район Нетании. Новые проекты и инвестиционные возможности.',
        h1: 'Недвижимость в Нат 600',
        intro: 'Нат 600 — быстро развивающийся район на севере Нетании.',
        content: ''
      }
    },
    'appartement-vue-mer-netanya': {
      fr: {
        title: 'Appartement Vue Mer Netanya | BAYITFULL - Biens d\'Exception',
        description: 'Appartements avec vue mer à Netanya. Sélection exclusive de biens face à la Méditerranée. BAYITFULL, votre spécialiste immobilier francophone.',
        h1: 'Appartements Vue Mer à Netanya',
        intro: 'Vivre face à la Méditerranée est un rêve accessible à Netanya. BAYITFULL vous propose une sélection d\'appartements avec vue mer, du studio au penthouse de luxe.',
        content: `
          <h2>Le privilège de la vue mer</h2>
          <p>Un appartement avec vue mer à Netanya, c'est se réveiller chaque matin face au bleu de la Méditerranée, profiter des couchers de soleil depuis son balcon et respirer l'air marin. C'est aussi un excellent investissement.</p>
          
          <h2>Où trouver une vue mer à Netanya ?</h2>
          <p>Les biens avec vue mer se concentrent principalement à <strong>Ir Yamim</strong>, sur le <strong>front de mer</strong> (טיילת) et dans certaines tours du <strong>centre-ville</strong>. Les étages élevés offrent les plus belles perspectives.</p>
          
          <h2>Budget pour une vue mer</h2>
          <p>Les appartements vue mer à Netanya débutent autour de 2 millions de shekels pour un 3 pièces en étage intermédiaire, et peuvent atteindre 10 millions et plus pour les penthouses de luxe à Ir Yamim.</p>
        `
      },
      en: {
        title: 'Sea View Apartment Netanya | BAYITFULL - Exceptional Properties',
        description: 'Sea view apartments in Netanya. Exclusive selection of properties facing the Mediterranean. BAYITFULL, your French-speaking real estate specialist.',
        h1: 'Sea View Apartments in Netanya',
        intro: 'Living facing the Mediterranean is an accessible dream in Netanya. BAYITFULL offers you a selection of sea view apartments, from studios to luxury penthouses.',
        content: ''
      },
      he: {
        title: 'דירה עם נוף לים נתניה | BAYITFULL',
        description: 'דירות עם נוף לים בנתניה. מבחר בלעדי של נכסים מול הים התיכון.',
        h1: 'דירות עם נוף לים בנתניה',
        intro: 'לחיות מול הים התיכון זה חלום נגיש בנתניה. BAYITFULL מציעה לכם מבחר דירות עם נוף לים.',
        content: ''
      },
      ru: {
        title: 'Квартира с видом на море Нетания | BAYITFULL',
        description: 'Квартиры с видом на море в Нетании. Эксклюзивный выбор недвижимости с видом на Средиземное море.',
        h1: 'Квартиры с видом на море в Нетании',
        intro: 'Жить с видом на Средиземное море — доступная мечта в Нетании.',
        content: ''
      }
    },
    'projets-neufs-netanya': {
      fr: {
        title: 'Projets Neufs Netanya | BAYITFULL - Achat sur Plan',
        description: 'Projets neufs et achat sur plan à Netanya. Programmes immobiliers des meilleurs promoteurs. BAYITFULL vous accompagne dans votre investissement.',
        h1: 'Projets Neufs à Netanya',
        intro: 'Acheter dans le neuf à Netanya présente de nombreux avantages : prix attractifs sur plan, appartements modernes aux dernières normes, garanties constructeur et possibilité de personnalisation.',
        content: `
          <h2>Pourquoi acheter dans le neuf ?</h2>
          <p>L'achat sur plan permet de bénéficier de <strong>prix inférieurs au marché</strong>, d'un <strong>échelonnement des paiements</strong> et d'un <strong>bien neuf sans travaux</strong>. C'est aussi l'assurance d'avoir un mamad, un parking et des finitions modernes.</p>
          
          <h2>Les quartiers en développement</h2>
          <p>Les principaux projets neufs se situent à <strong>Nat 600</strong> au nord de Netanya, mais aussi dans le <strong>centre-ville</strong> en rénovation et sur certains terrains d'<strong>Ir Yamim</strong>.</p>
          
          <h2>Comment acheter sur plan en Israël ?</h2>
          <p>BAYITFULL vous accompagne dans toutes les étapes : choix du projet, sélection de l'appartement, négociation avec le promoteur, suivi juridique avec un avocat francophone et livraison du bien.</p>
        `
      },
      en: {
        title: 'New Projects Netanya | BAYITFULL - Off-Plan Purchase',
        description: 'New projects and off-plan purchases in Netanya. Real estate programs from the best developers. BAYITFULL supports you in your investment.',
        h1: 'New Projects in Netanya',
        intro: 'Buying new in Netanya has many advantages: attractive off-plan prices, modern apartments to the latest standards, builder guarantees and customization options.',
        content: ''
      },
      he: {
        title: 'פרויקטים חדשים נתניה | BAYITFULL',
        description: 'פרויקטים חדשים ורכישה מקבלן בנתניה. תוכניות נדל"ן מהקבלנים הטובים ביותר.',
        h1: 'פרויקטים חדשים בנתניה',
        intro: 'קנייה בפרויקט חדש בנתניה מציעה יתרונות רבים: מחירים אטרקטיביים מקבלן, דירות מודרניות.',
        content: ''
      },
      ru: {
        title: 'Новостройки Нетания | BAYITFULL',
        description: 'Новые проекты и покупка на этапе строительства в Нетании.',
        h1: 'Новостройки в Нетании',
        intro: 'Покупка новостройки в Нетании имеет много преимуществ.',
        content: ''
      }
    },
    'vente-appartement-netanya': {
      fr: {
        title: 'Vente Appartement Netanya | BAYITFULL - Acheter à Netanya',
        description: 'Appartements à vendre à Netanya. Large choix de biens dans tous les quartiers. BAYITFULL, votre agent immobilier francophone pour acheter en Israël.',
        h1: 'Appartements à Vendre à Netanya',
        intro: 'Vous souhaitez acheter un appartement à Netanya ? BAYITFULL vous accompagne dans votre recherche avec une sélection de biens correspondant à vos critères et votre budget.',
        content: `
          <h2>Acheter un appartement à Netanya</h2>
          <p>Netanya offre une grande diversité de biens à la vente : du studio pour investissement locatif à la villa de prestige, en passant par les appartements familiaux et les penthouses avec vue mer.</p>
          
          <h2>Les étapes de l'achat immobilier en Israël</h2>
          <p>L'achat immobilier en Israël suit un processus spécifique : <strong>recherche du bien</strong>, <strong>négociation</strong>, <strong>vérification juridique</strong> par un avocat, <strong>signature chez le notaire</strong> et <strong>transfert de propriété</strong> au Tabu.</p>
          
          <h2>Financement et fiscalité</h2>
          <p>Les banques israéliennes proposent des prêts immobiliers aux résidents et non-résidents. BAYITFULL vous oriente vers les bons interlocuteurs : banquiers, avocats et comptables francophones.</p>
        `
      },
      en: {
        title: 'Apartments for Sale Netanya | BAYITFULL - Buy in Netanya',
        description: 'Apartments for sale in Netanya. Wide choice of properties in all neighborhoods. BAYITFULL, your French-speaking real estate agent for buying in Israel.',
        h1: 'Apartments for Sale in Netanya',
        intro: 'Looking to buy an apartment in Netanya? BAYITFULL supports you in your search with a selection of properties matching your criteria and budget.',
        content: ''
      },
      he: {
        title: 'דירות למכירה נתניה | BAYITFULL',
        description: 'דירות למכירה בנתניה. מבחר רחב של נכסים בכל השכונות.',
        h1: 'דירות למכירה בנתניה',
        intro: 'מחפשים לקנות דירה בנתניה? BAYITFULL מלווה אתכם בחיפוש עם מבחר נכסים התואמים את הקריטריונים והתקציב שלכם.',
        content: ''
      },
      ru: {
        title: 'Квартиры на продажу Нетания | BAYITFULL',
        description: 'Квартиры на продажу в Нетании. Большой выбор недвижимости во всех районах.',
        h1: 'Квартиры на продажу в Нетании',
        intro: 'Хотите купить квартиру в Нетании? BAYITFULL поможет вам в поиске.',
        content: ''
      }
    },
    'location-appartement-netanya': {
      fr: {
        title: 'Location Appartement Netanya | BAYITFULL - Louer à Netanya',
        description: 'Appartements à louer à Netanya. Location longue durée et saisonnière. BAYITFULL trouve le bien idéal pour votre séjour ou installation en Israël.',
        h1: 'Appartements à Louer à Netanya',
        intro: 'Vous cherchez un appartement à louer à Netanya ? Que ce soit pour une location longue durée ou un séjour de quelques mois, BAYITFULL vous aide à trouver le logement idéal.',
        content: `
          <h2>Louer un appartement à Netanya</h2>
          <p>Le marché locatif à Netanya est dynamique avec une offre variée : appartements meublés ou non, locations à l'année ou saisonnières, dans tous les quartiers de la ville.</p>
          
          <h2>Location longue durée vs saisonnière</h2>
          <p>La <strong>location longue durée</strong> (12 mois minimum) offre des loyers plus avantageux. La <strong>location saisonnière</strong> convient aux séjours de quelques semaines à quelques mois, notamment l'été.</p>
          
          <h2>Budget location à Netanya</h2>
          <p>Les loyers à Netanya varient selon le quartier et le type de bien : comptez environ 4000-6000 ₪/mois pour un 3 pièces en centre-ville, et 8000-15000 ₪/mois pour un bien de standing à Ir Yamim.</p>
        `
      },
      en: {
        title: 'Apartments for Rent Netanya | BAYITFULL - Rent in Netanya',
        description: 'Apartments for rent in Netanya. Long-term and seasonal rentals. BAYITFULL finds the ideal property for your stay or relocation to Israel.',
        h1: 'Apartments for Rent in Netanya',
        intro: 'Looking for an apartment to rent in Netanya? Whether for long-term rental or a few months stay, BAYITFULL helps you find the ideal accommodation.',
        content: ''
      },
      he: {
        title: 'דירות להשכרה נתניה | BAYITFULL',
        description: 'דירות להשכרה בנתניה. השכרה ארוכת טווח ועונתית.',
        h1: 'דירות להשכרה בנתניה',
        intro: 'מחפשים דירה להשכרה בנתניה? בין אם להשכרה ארוכת טווח או לכמה חודשים, BAYITFULL עוזרת לכם למצוא את המגורים האידיאליים.',
        content: ''
      },
      ru: {
        title: 'Аренда квартир Нетания | BAYITFULL',
        description: 'Квартиры в аренду в Нетании. Долгосрочная и сезонная аренда.',
        h1: 'Аренда квартир в Нетании',
        intro: 'Ищете квартиру в аренду в Нетании? BAYITFULL поможет найти идеальное жилье.',
        content: ''
      }
    }
  };

  const currentPath = location.pathname.replace('/', '') || 'immobilier-netanya';
  const config = pageConfigs[currentPath] || pageConfigs['immobilier-netanya'];
  const pageContent = config[language] || config.fr;

  // Breadcrumb data
  const breadcrumbs = [
    { name: language === 'fr' ? 'Accueil' : language === 'he' ? 'בית' : 'Home', url: 'https://www.bayitfull.com/' },
    { name: pageContent.h1, url: `https://www.bayitfull.com/${currentPath}` }
  ];

  // Scroll to top on page change and set document title
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = pageContent.title;
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', pageContent.description);
    }
    
    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `https://www.bayitfull.com/${currentPath}`);
    }
  }, [location.pathname, pageContent.title, pageContent.description, currentPath]);

  return (
    <div className={`min-h-screen bg-[#fafaf9] ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/98 backdrop-blur-md border-b border-gray-100">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <span className="text-2xl font-semibold tracking-tight">
              <span className="text-[#6b21a8]">BAYIT</span><span className="text-gray-800">FULL</span>
            </span>
          </Link>
          <div className="hidden lg:flex items-center space-x-6 text-sm tracking-wide text-gray-600">
            <Link to="/" className="hover:text-[#6b21a8] transition-colors font-medium">
              {language === 'fr' ? 'Accueil' : language === 'he' ? 'בית' : language === 'ru' ? 'Главная' : 'Home'}
            </Link>
            <Link to="/immobilier-netanya" className="hover:text-[#6b21a8] transition-colors font-medium">
              {language === 'fr' ? 'Netanya' : language === 'he' ? 'נתניה' : 'Netanya'}
            </Link>
            <Link to="/ir-yamim" className="hover:text-[#6b21a8] transition-colors font-medium">Ir Yamim</Link>
            <Link to="/nat-600" className="hover:text-[#6b21a8] transition-colors font-medium">Nat 600</Link>
            <Link to="/projets-neufs-netanya" className="hover:text-[#6b21a8] transition-colors font-medium">
              {language === 'fr' ? 'Projets Neufs' : language === 'he' ? 'פרויקטים חדשים' : 'New Projects'}
            </Link>
          </div>
          <div className="flex items-center space-x-2">
            {['FR', 'EN', 'HE', 'RU'].map(lang => (
              <button
                key={lang}
                onClick={() => changeLanguage(lang.toLowerCase())}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  language === lang.toLowerCase() 
                    ? 'bg-[#6b21a8] text-white' 
                    : 'text-gray-500 hover:text-[#6b21a8]'
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-20 bg-white border-b border-gray-100">
        <div className="container mx-auto px-6 py-3">
          <nav className="flex items-center text-sm text-gray-500">
            <Link to="/" className="hover:text-[#6b21a8]">
              <Home className="w-4 h-4" />
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#6b21a8] font-medium">{pageContent.h1}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#6b21a8] to-[#581c87] text-white py-16">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-light mb-6">{pageContent.h1}</h1>
          <p className="text-xl text-white/90 max-w-3xl leading-relaxed">{pageContent.intro}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl">
            <div 
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-headings:font-light prose-p:text-gray-600 prose-strong:text-[#6b21a8]"
              dangerouslySetInnerHTML={{ __html: pageContent.content }}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-light text-gray-900 mb-6">
              {language === 'fr' ? 'Votre projet immobilier commence ici' : 
               language === 'he' ? 'הפרויקט הנדל"ני שלכם מתחיל כאן' :
               language === 'ru' ? 'Ваш проект недвижимости начинается здесь' :
               'Your real estate project starts here'}
            </h2>
            <p className="text-gray-600 mb-8">
              {language === 'fr' ? 'Contactez Vanessa Benaim Kakun pour discuter de votre projet.' :
               language === 'he' ? 'צרו קשר עם ונסה בנאים קקון כדי לדון בפרויקט שלכם.' :
               language === 'ru' ? 'Свяжитесь с Ванессой Бенаим Какун, чтобы обсудить ваш проект.' :
               'Contact Vanessa Benaim Kakun to discuss your project.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                className="bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-4"
                onClick={() => openWhatsApp()}
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Vanessa
              </Button>
              <a href="tel:+972523570058" className="flex items-center text-[#6b21a8] hover:text-[#581c87]">
                <Phone className="w-5 h-5 mr-2" />
                052 357 0058
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-16 bg-[#fafaf9]">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-light text-gray-900 mb-8 text-center">
            {language === 'fr' ? 'Explorez aussi' : 
             language === 'he' ? 'גלו גם' :
             language === 'ru' ? 'Также исследуйте' :
             'Also explore'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {currentPath !== 'immobilier-netanya' && (
              <Link to="/immobilier-netanya" className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-center">
                <MapPin className="w-6 h-6 text-[#6b21a8] mx-auto mb-2" />
                <span className="text-sm text-gray-700">Netanya</span>
              </Link>
            )}
            {currentPath !== 'ir-yamim' && (
              <Link to="/ir-yamim" className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-center">
                <Waves className="w-6 h-6 text-[#6b21a8] mx-auto mb-2" />
                <span className="text-sm text-gray-700">Ir Yamim</span>
              </Link>
            )}
            {currentPath !== 'nat-600' && (
              <Link to="/nat-600" className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-center">
                <Building2 className="w-6 h-6 text-[#6b21a8] mx-auto mb-2" />
                <span className="text-sm text-gray-700">Nat 600</span>
              </Link>
            )}
            {currentPath !== 'vente-appartement-netanya' && (
              <Link to="/vente-appartement-netanya" className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-center">
                <Key className="w-6 h-6 text-[#6b21a8] mx-auto mb-2" />
                <span className="text-sm text-gray-700">{language === 'fr' ? 'Acheter' : 'Buy'}</span>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1f2937] py-12">
        <div className="container mx-auto px-6 text-center">
          <span className="text-2xl font-semibold tracking-tight">
            <span className="text-[#a78bfa]">BAYIT</span><span className="text-white">FULL</span>
          </span>
          <p className="text-gray-400 mt-4">Vanessa Benaim Kakun</p>
          <p className="text-gray-500 mt-2">052 357 0058 | vanessa.kakun@gmail.com</p>
          <p className="text-gray-500 mt-1">Netanya, Israel</p>
          <p className="text-gray-600 text-sm mt-8">© 2025 BAYITFULL. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <button
        onClick={() => openWhatsApp()}
        className="fixed bottom-6 right-6 z-40 bg-[#25D366] hover:bg-[#128C7E] text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-7 h-7" />
      </button>
    </div>
  );
};

export default SEOPage;
